import { z } from 'zod';
import { isoDay as day, isoInstant as instant } from './values.js';
/**
 * Who represents a client and whom it belongs to (ADR-047): kept at the client, not in its intake -
 * so a new managing director is recorded after the intake closed. Bitemporal (ADR-003): when a
 * role held (`validFrom`, `validUntil`) apart from when the firm learnt it; a change is a new
 * entry, the old one stays in the history.
 */
export const ClientRoleKind = z
    .enum(['managing_director', 'shareholder', 'procurator'])
    .meta({ id: 'ClientRoleKind' });
export const clientRoleKindLabel = {
    managing_director: 'Geschäftsführung',
    shareholder: 'Gesellschafter',
    procurator: 'Prokura',
};
/** Where the firm has it from. */
export const ClientRoleSource = z.enum(['firm', 'portal', 'register']).meta({
    id: 'ClientRoleSource',
});
export const clientRoleSourceLabel = {
    firm: 'Kanzlei',
    portal: 'Mandant im Portal',
    register: 'Handelsregister',
};
export const ClientRoleInput = z
    .object({
    kind: ClientRoleKind,
    /** A person's or - for a shareholder - a company's name. */
    name: z.string().trim().min(2).max(200),
    /** A person's; `null` for a company, or not stated. */
    birthDate: day.nullable(),
    /** Hundredths of a per cent, shareholders only; `null`: not stated. */
    shareBasisPoints: z.int().min(0).max(10_000).nullable(),
    /** `null`: not known. */
    validFrom: day.nullable(),
    /** `null`: holds on, or not known to end. */
    validUntil: day.nullable(),
    source: ClientRoleSource,
    /** A known person of the client (ADR-051); left out, found by name and birth date or new. */
    personId: z.uuid().exactOptional(),
})
    .refine((r) => r.shareBasisPoints === null || r.kind === 'shareholder', {
    path: ['shareBasisPoints'],
    message: 'Only a shareholder holds a share.',
})
    .refine((r) => !r.validFrom || !r.validUntil || r.validFrom <= r.validUntil, {
    path: ['validUntil'],
    message: 'A role ends after it began.',
})
    .meta({ id: 'ClientRoleInput' });
/** A role as the firm knows it now. */
export const ClientRole = z
    .object({
    id: z.uuid(),
    /** The person it is a role of (ADR-051). */
    personId: z.uuid(),
    kind: ClientRoleKind,
    name: z.string(),
    birthDate: day.nullable(),
    shareBasisPoints: z.int().nullable(),
    validFrom: day.nullable(),
    validUntil: day.nullable(),
    source: ClientRoleSource,
    /** When the firm recorded this entry, and who - by Keycloak `sub`. */
    recordedAt: instant,
    recordedBy: z.string(),
})
    .meta({ id: 'ClientRole' });
export const ClientRoleList = z.array(ClientRole).meta({ id: 'ClientRoleList' });
/** Every entry ever made, the replaced and removed ones with when, by whom and by which. */
export const ClientRoleEntry = ClientRole.extend({
    supersededAt: instant.nullable(),
    supersededBy: z.string().nullable(),
    /** The entry that replaced it; `null` while current, or for a removal. */
    replacedBy: z.uuid().nullable(),
}).meta({ id: 'ClientRoleEntry' });
export const ClientRoleHistory = z.array(ClientRoleEntry).meta({ id: 'ClientRoleHistory' });
export const ClientRoleParams = z
    .object({ caseId: z.uuid(), roleId: z.uuid() })
    .meta({ id: 'ClientRoleParams' });
//# sourceMappingURL=client-roles.js.map